import { Option } from 'effect';

function calculateDiscountPercentage(
  price: number,
  originalPrice: number | null,
  minimumDiscountPercentage: number
): number | null {
  const hasOriginalPrice = originalPrice !== null && originalPrice > price;

  if (!hasOriginalPrice) {
    return null;
  }

  const result = ((originalPrice - price) / originalPrice) * 100;
  if (result < minimumDiscountPercentage) return null;

  return Math.round(result);
}

/**
 * Card-shaped discount: `price` is `None` for a variant with no channel
 * price. A raw `number` cannot be passed here by accident, which is what let
 * a fabricated `0` reach {@link calculateDiscountPercentage} unguarded on both
 * the product card (issue 219) and the PDP (issue 242) before this split. `0`
 * is treated the same as absence — a genuinely free item has no "original
 * price it was discounted from" to advertise as a badge.
 */
export function getCardDiscountPercentage(
  price: Option.Option<number>,
  originalPrice: number | null,
  minimumDiscountPercentage = 2
): number | null {
  return Option.match(price, {
    onNone: () => null,
    onSome: (value) =>
      value === 0
        ? null
        : calculateDiscountPercentage(value, originalPrice, minimumDiscountPercentage),
  });
}

/**
 * Coupon-shaped discount: both prices are always present, and `priceWithCode`
 * legitimately reaches `0` for a 100%-off coupon — that case must still
 * compute a discount, so this entry point stays unguarded.
 */
export function getCouponDiscountPercentage(
  priceWithCode: number,
  originalPrice: number,
  minimumDiscountPercentage = 2
): number | null {
  return calculateDiscountPercentage(priceWithCode, originalPrice, minimumDiscountPercentage);
}

export function shouldDisplayOriginalPrice(
  price: number,
  originalPrice: number | null,
  minimumDiscountPercentage = 2
) {
  if (price === 0) return false;

  const priceAmount = price;
  const originalPriceAmount = originalPrice ?? null;

  const hasOriginalPrice = originalPriceAmount !== null && originalPriceAmount > priceAmount;

  if (!hasOriginalPrice) {
    return null;
  }

  const result = ((originalPriceAmount - priceAmount) / originalPriceAmount) * 100;
  return result >= minimumDiscountPercentage;
}
